import useAppStore from "@/context/store";
import { safeParse } from "@/utils/safeParse";
import { useLocalStorageState, useSafeState } from "ahooks";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useCallback, useEffect, useMemo } from "react";
dayjs.extend(duration);

/**
 * loadingValue为加载用户信息这段时间的默认值；
 * defaultValue为localStorage为空的时候默认值；
 * initValue 为 没取localStorage时的值；
 * @param {*} generateKey
 * @param {{
 *  needUserId?: boolean,
 *  loadingValue?: any,
 *  initValue?: any,
 *  defaultValue: any?
 * }} params
 * @returns
 */
export default function useLocalStorage(generateKey, params) {
  const [value, setValue] = useSafeState(
    params?.needUserId ? params?.loadingValue : params?.initValue
  );
  const user = useAppStore((store) => store.user);
  const userId = user?.user_id;

  useEffect(() => {
    if (params?.needUserId) {
      if (userId) {
        const key =
          typeof generateKey === "string" ? generateKey : generateKey(userId);
        const value = safeParse(window.localStorage.getItem(key));
        setValue(value === null ? params?.defaultValue : value);
      } else {
        setValue(params?.loadingValue);
      }
    } else {
      const key = typeof generateKey === "string" ? generateKey : generateKey();
      const value = safeParse(window.localStorage.getItem(key));
      setValue(value === null ? params?.defaultValue : value);
    }
  }, [generateKey, params, userId, setValue]);

  const setState = useCallback(
    (value) => {
      if (params?.needUserId) {
        if (userId) {
          const key =
            typeof generateKey === "string" ? generateKey : generateKey(userId);
          window.localStorage.setItem(key, JSON.stringify(value));
          setValue(value);
        }
      } else {
        const key =
          typeof generateKey === "string" ? generateKey : generateKey();
        window.localStorage.setItem(key, JSON.stringify(value));
        setValue(value);
      }
    },
    [generateKey, params, userId, setValue]
  );

  return [value, setState];
}

/**
 * 带过期时间戳的localStorage, expire：毫秒 默认当日后过期
 * @param {string} key
 * @param {{
 *  defaultValue: any
 *  expire?: number
 * }} params
 * @returns
 */
export function useLocalStorageWithExpired(key, params) {
  const [state, setState] = useLocalStorageState(key, {
    defaultValue: {
      value: params?.defaultValue,
      expired:
        Date.now() + (params?.expire || dayjs().endOf("day").diff(dayjs())),
    },
    ...params,
  });

  const value = useMemo(() => {
    // 过期后置为默认值
    return state?.expired > Date.now() ? state?.value : params?.defaultValue;
  }, [params?.defaultValue, state?.expired, state?.value]);

  const setValue = useCallback(
    (value) => {
      setState({
        value,
        // 自动设置过期时间
        expired:
          Date.now() + (params?.expire || dayjs().endOf("day").diff(dayjs())),
      });
    },
    [params?.expire, setState]
  );

  return [value, setValue];
}
